import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, unwrap, unwrapPage } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { usePestana } from '@/hooks/usePestana';
import { cn } from '@/lib/utils';

type Clave = 'specialties' | 'universities' | 'heard-about' | 'issue-types' | 'enarm-scores';
type Valor = string | number | boolean | null;
type Fila = Record<string, Valor> & { id: number; usage: number };

interface CampoDef {
  k: string;
  etiqueta: string;
  tipo: 'texto' | 'numero' | 'casilla';
  max?: number;
  requerido?: boolean;
}

const CATALOGOS: Record<Clave, { titulo: string; ayuda: string; campos: CampoDef[] }> = {
  specialties: {
    titulo: 'Especialidades',
    ayuda:
      'Las usan los casos, clases, temas y la biblioteca. Renombrar se refleja en todo el sistema.',
    campos: [
      { k: 'name', etiqueta: 'Especialidad', tipo: 'texto', max: 80, requerido: true },
      { k: 'subspecialty', etiqueta: 'Subespecialidad', tipo: 'texto', max: 100 },
    ],
  },
  universities: {
    titulo: 'Universidades',
    ayuda: 'Las que el alumno elige al registrarse. Las verificadas son las del catálogo oficial.',
    campos: [
      { k: 'name', etiqueta: 'Nombre', tipo: 'texto', max: 150, requerido: true },
      { k: 'countryText', etiqueta: 'País', tipo: 'texto', max: 60 },
      { k: 'isVerified', etiqueta: 'Verificada', tipo: 'casilla' },
    ],
  },
  'heard-about': {
    titulo: '¿Cómo nos conociste?',
    ayuda: 'Opciones del registro.',
    campos: [{ k: 'name', etiqueta: 'Opción', tipo: 'texto', max: 60, requerido: true }],
  },
  'issue-types': {
    titulo: 'Tipos de reporte',
    ayuda: 'Lo que el alumno elige al reportar un caso clínico.',
    campos: [
      { k: 'description', etiqueta: 'Descripción', tipo: 'texto', max: 150, requerido: true },
    ],
  },
  'enarm-scores': {
    titulo: 'Puntajes ENARM',
    ayuda:
      'Puntaje mínimo y máximo de seleccionados por especialidad y año (los ve el alumno en su perfil).',
    campos: [
      { k: 'specialtyName', etiqueta: 'Especialidad', tipo: 'texto', max: 80, requerido: true },
      { k: 'year', etiqueta: 'Año', tipo: 'numero', requerido: true },
      { k: 'minScore', etiqueta: 'Mínimo', tipo: 'numero', requerido: true },
      { k: 'maxScore', etiqueta: 'Máximo', tipo: 'numero', requerido: true },
    ],
  },
};

const PESTANAS = (Object.keys(CATALOGOS) as Clave[]).map((k) => [k, CATALOGOS[k].titulo] as const);

/* Las cinco rutas tienen la misma forma; se tipan con la de especialidades. */
type RutaLista = '/api/v1/admin/catalogs/specialties';
type RutaId = '/api/v1/admin/catalogs/specialties/{id}';
const lista = (c: Clave) => `/api/v1/admin/catalogs/${c}` as RutaLista;
const conId = (c: Clave) => `/api/v1/admin/catalogs/${c}/{id}` as RutaId;

function ModalFila({
  clave,
  fila,
  onCerrar,
}: {
  clave: Clave;
  fila: Fila | null;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  const def = CATALOGOS[clave];
  const [f, setF] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(
      def.campos.map((c) => [
        c.k,
        c.tipo === 'casilla' ? Boolean(fila?.[c.k]) : String(fila?.[c.k] ?? ''),
      ]),
    ),
  );
  const guardar = useMutation({
    mutationFn: () => {
      const body: Record<string, Valor> = Object.fromEntries(
        def.campos.map((c): [string, Valor] => {
          const v = f[c.k];
          if (c.tipo === 'casilla') return [c.k, Boolean(v)];
          if (c.tipo === 'numero') return [c.k, Number(v)];
          const s = String(v).trim();
          return [c.k, s === '' && !c.requerido ? null : s];
        }),
      );
      return fila
        ? unwrap(
            api.PATCH(conId(clave), {
              params: { path: { id: String(fila.id) } },
              body: body as never,
            }),
          )
        : unwrap(api.POST(lista(clave), { body: body as never }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalogos', clave] });
      void qc.invalidateQueries({ queryKey: ['especialidades'] });
      toast.success('Guardado.');
      onCerrar();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={fila ? `Editar: ${def.titulo}` : `Agregar: ${def.titulo}`}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate();
        }}
      >
        {def.campos.map((c) =>
          c.tipo === 'casilla' ? (
            <label key={c.k} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--color-azul-600)]"
                checked={Boolean(f[c.k])}
                onChange={(e) => setF({ ...f, [c.k]: e.target.checked })}
              />
              {c.etiqueta}
            </label>
          ) : (
            <Campo key={c.k} etiqueta={c.requerido ? `${c.etiqueta} *` : c.etiqueta}>
              <input
                className="input-vidrio"
                type={c.tipo === 'numero' ? 'number' : 'text'}
                step={c.tipo === 'numero' ? 'any' : undefined}
                required={c.requerido}
                maxLength={c.max}
                value={String(f[c.k] ?? '')}
                onChange={(e) => setF({ ...f, [c.k]: e.target.value })}
              />
            </Campo>
          ),
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CopiarAnio({ anios, onCerrar }: { anios: number[]; onCerrar: () => void }) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(anios[0] ?? new Date().getFullYear() - 1);
  const [to, setTo] = useState((anios[0] ?? new Date().getFullYear() - 1) + 1);
  const copiar = useMutation({
    mutationFn: () =>
      unwrap<{ copied: number }>(
        api.POST('/api/v1/admin/catalogs/enarm-scores/copy-year', { body: { from, to } }),
      ),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['catalogos', 'enarm-scores'] });
      toast.success(`${r.copied} puntaje(s) copiados a ${to}. Corrige los que cambiaron.`);
      onCerrar();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Copiar puntajes de un año"
      descripcion="Para capturar el año nuevo partiendo del anterior."
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Del año">
          <select
            className="input-vidrio"
            value={from}
            onChange={(e) => setFrom(Number(e.target.value))}
          >
            {anios.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Al año">
          <input
            className="input-vidrio"
            type="number"
            value={to}
            onChange={(e) => setTo(Number(e.target.value))}
          />
        </Campo>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn-cs btn-primario"
          disabled={copiar.isPending}
          onClick={() => copiar.mutate()}
        >
          Copiar
        </button>
      </div>
    </Modal>
  );
}

function Tabla({ clave }: { clave: Clave }) {
  const editable = useCan('configuracion', 'editar');
  const qc = useQueryClient();
  const def = CATALOGOS[clave];
  const [texto, setTexto] = useState('');
  const [page, setPage] = useState(1);
  const [anio, setAnio] = useState<number | null>(null);
  const [modal, setModal] = useState<{ fila: Fila | null } | null>(null);
  const [borrar, setBorrar] = useState<Fila | null>(null);
  const [copiando, setCopiando] = useState(false);
  const q = useQuery({
    queryKey: ['catalogos', clave, texto, page, anio],
    queryFn: () =>
      unwrapPage<{ items: Fila[]; years?: number[] }>(
        api.GET(lista(clave), {
          params: {
            query: {
              q: texto || undefined,
              page,
              pageSize: 50,
              ...(anio ? { year: anio } : {}),
            } as never,
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const quitar = useMutation({
    mutationFn: (id: number) =>
      unwrap(api.DELETE(conId(clave), { params: { path: { id: String(id) } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalogos', clave] });
      setBorrar(null);
      toast.success('Borrado.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const anios = q.data?.data.years ?? [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-texto-2">{def.ayuda}</p>
      <div className="flex flex-wrap items-end gap-2">
        <Campo etiqueta="Buscar">
          <input
            className="input-vidrio"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setPage(1);
            }}
          />
        </Campo>
        {clave === 'enarm-scores' && (
          <Campo etiqueta="Año">
            <select
              className="input-vidrio"
              value={anio ?? ''}
              onChange={(e) => setAnio(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Todos</option>
              {anios.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Campo>
        )}
        {editable && (
          <div className="ml-auto flex gap-2">
            {clave === 'enarm-scores' && anios.length > 0 && (
              <button type="button" className="btn-cs btn-vidrio" onClick={() => setCopiando(true)}>
                <Copy size={16} aria-hidden /> Copiar año
              </button>
            )}
            <button
              type="button"
              className="btn-cs btn-primario"
              onClick={() => setModal({ fila: null })}
            >
              <Plus size={16} aria-hidden /> Agregar
            </button>
          </div>
        )}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.items.length === 0 && <Vacio titulo="Sin registros" />}
      {q.data && q.data.data.items.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                {clave === 'issue-types' && <th>Clave</th>}
                {def.campos.map((c) => (
                  <th key={c.k}>{c.etiqueta}</th>
                ))}
                <th className="text-right">En uso</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {q.data.data.items.map((r) => (
                <tr key={r.id}>
                  {clave === 'issue-types' && <td className="tabular">{String(r.key)}</td>}
                  {def.campos.map((c) => (
                    <td key={c.k} className={cn(c.tipo === 'numero' && 'tabular')}>
                      {c.tipo === 'casilla' ? (r[c.k] ? 'Sí' : 'No') : String(r[c.k] ?? '—')}
                    </td>
                  ))}
                  <td className="tabular text-right">{r.usage}</td>
                  {editable && (
                    <td className="whitespace-nowrap text-right">
                      <button
                        type="button"
                        className="btn-cs btn-vidrio px-2 py-1"
                        aria-label="Editar"
                        onClick={() => setModal({ fila: r })}
                      >
                        <Pencil size={14} aria-hidden />
                      </button>{' '}
                      <button
                        type="button"
                        className="btn-cs btn-vidrio px-2 py-1 text-rojo-texto"
                        aria-label="Borrar"
                        disabled={r.usage > 0}
                        title={r.usage > 0 ? 'Está en uso: no se puede borrar' : undefined}
                        onClick={() => setBorrar(r)}
                      >
                        <Trash2 size={14} aria-hidden />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={setPage}
        />
      )}
      {modal && <ModalFila clave={clave} fila={modal.fila} onCerrar={() => setModal(null)} />}
      {copiando && <CopiarAnio anios={anios} onCerrar={() => setCopiando(false)} />}
      <ConfirmarAccion
        abierto={borrar !== null}
        onCerrar={() => setBorrar(null)}
        titulo="Borrar del catálogo"
        descripcion="Nadie lo usa; se borra definitivamente."
        textoBoton="Borrar"
        peligro
        ocupado={quitar.isPending}
        onConfirmar={() => borrar && quitar.mutate(borrar.id)}
      />
    </div>
  );
}

function Catalogos() {
  const [pestana, setPestana] = usePestana(PESTANAS);
  return (
    <>
      <Encabezado
        titulo="Catálogos"
        descripcion="Listas que usan el registro de alumnos, la plataforma y el banco de casos."
      />
      <div role="tablist" aria-label="Catálogos" className="mb-5 flex flex-wrap gap-2">
        {PESTANAS.map(([k, t]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={pestana === k}
            className={cn('btn-cs', pestana === k ? 'btn-primario' : 'btn-vidrio')}
            onClick={() => setPestana(k)}
          >
            {t}
          </button>
        ))}
      </div>
      <Tabla key={pestana} clave={pestana} />
    </>
  );
}

export function CatalogosPage() {
  return (
    <PermissionGate seccion="configuracion">
      <Catalogos />
    </PermissionGate>
  );
}
